import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { History, Plus, Download, StickyNote, FolderPlus, FilePlus, Edit3, CheckCircle, Loader2, X, Trash2 } from 'lucide-react';

const TYPE_META = {
  created:        { icon: CheckCircle, color: 'text-[#A3FF12]',  label: 'Creado' },
  renamed:        { icon: Edit3,       color: 'text-blue-300',   label: 'Actualizado' },
  folder_created: { icon: FolderPlus,  color: 'text-purple-300', label: 'Carpeta' },
  doc_uploaded:   { icon: FilePlus,    color: 'text-cyan-300',   label: 'Documento' },
  note:           { icon: StickyNote,  color: 'text-yellow-300', label: 'Nota' },
};

const ProjectTimeline = ({ projectId, onClose }) => {
  const { user } = useAuth();
  const canWrite = user && (user.role === 'admin' || user.role === 'gestor');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNote, setShowNote] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteDesc, setNoteDesc] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/site-projects/${projectId}/timeline`);
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const addNote = async () => {
    if (!noteTitle.trim()) return;
    try {
      await api.post(`/site-projects/${projectId}/timeline/note`, {
        title: noteTitle.trim(), description: noteDesc.trim(),
      });
      setNoteTitle(''); setNoteDesc(''); setShowNote(false);
      toast.success('Nota añadida');
      load();
    } catch { toast.error('Error añadiendo nota'); }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    await api.delete(`/site-projects/timeline/${eventId}`);
    load();
  };

  const exportCsv = async () => {
    const r = await api.get(`/site-projects/${projectId}/timeline/export.csv`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timeline-${(data?.project?.name || 'proyecto').replace(/[^a-z0-9]/gi, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center py-6"><Loader2 className="animate-spin text-[#A3FF12]" size={20}/></div>;
  if (!data) return null;

  const s = data.summary || {};
  return (
    <div className="bg-black/60 border border-white/10 rounded-xl p-3 mt-2" data-testid={`timeline-${projectId}`}>
      <div className="flex items-center gap-2 mb-2">
        <History size={14} className="text-[#A3FF12]"/>
        <h5 className="text-xs font-black uppercase tracking-widest text-white/70">Seguimiento</h5>
        <span className="text-[10px] font-bold text-white/40 ml-1">{s.total || 0} eventos</span>
        <div className="flex-1"/>
        {canWrite && (
          <button onClick={() => setShowNote(!showNote)}
            className="text-[10px] font-bold uppercase inline-flex items-center gap-1 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 px-2 py-1 rounded-full"
            data-testid="btn-add-note">
            <Plus size={10}/> Nota
          </button>
        )}
        <button onClick={exportCsv} className="text-[10px] font-bold uppercase inline-flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-2 py-1 rounded-full" data-testid="btn-export-timeline">
          <Download size={10}/> CSV
        </button>
        {onClose && <button onClick={onClose} className="text-white/40 hover:text-white ml-1"><X size={14}/></button>}
      </div>

      {/* Summary bar */}
      {Object.keys(s.by_type || {}).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2 pb-2 border-b border-white/5">
          {Object.entries(s.by_type).map(([type, count]) => {
            const meta = TYPE_META[type] || {};
            return (
              <span key={type} className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 ${meta.color || 'text-white/60'}`}>
                {meta.label || type}: {count}
              </span>
            );
          })}
        </div>
      )}

      {showNote && (
        <div className="bg-black/40 border border-yellow-500/30 rounded-lg p-2 mb-2 space-y-1.5" data-testid="note-form">
          <input value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder="Título de la nota *" className="w-full bg-black border border-white/10 rounded px-2 py-1 text-xs text-white outline-none"/>
          <textarea value={noteDesc} onChange={(e) => setNoteDesc(e.target.value)} placeholder="Detalles (opcional)" rows={2} className="w-full bg-black border border-white/10 rounded px-2 py-1 text-xs text-white outline-none"/>
          <button onClick={addNote} className="bg-yellow-500 text-black px-3 py-1 rounded text-[11px] font-bold hover:bg-yellow-400">Añadir</button>
        </div>
      )}

      {data.events.length === 0 ? (
        <div className="text-center py-3 text-white/40 text-xs">Sin actividad todavía.</div>
      ) : (
        <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1">
          {data.events.map((e) => {
            const meta = TYPE_META[e.type] || { icon: History, color: 'text-white/50', label: e.type };
            const Icon = meta.icon;
            return (
              <div key={e.id} className="flex items-start gap-2 text-xs py-1.5 border-b border-white/5 last:border-b-0 group" data-testid={`timeline-event-${e.id}`}>
                <Icon className={`${meta.color} flex-shrink-0 mt-0.5`} size={13}/>
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">{e.title}</div>
                  {e.description && <div className="text-white/50 text-[11px] truncate italic">{e.description}</div>}
                  <div className="text-white/30 text-[10px]">{new Date(e.created_at).toLocaleString('es-ES')} · {e.user_name || '—'}</div>
                </div>
                {canWrite && (
                  <button onClick={() => deleteEvent(e.id)} className="text-white/20 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={10}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectTimeline;
